export const optionStepParts = [
  'slotCard',
  'slotCardEmpty',
  'slotCardSelected',
  'slotCardImage',
  'slotCardImagePlaceholder',
  'slotCardChooseLink',
  'nameLine1',
  'nameLine2',
  'nameLineEmptyTitle',
  'nameLineEmptyHint',
  'nameLineEmptySlotLabel',
  'radioOption',
  'radioOptionSelected',
  'radioOptionImage',
  'radioOptionImagePlaceholder',
  'radioOptionExtraPriceContainer',
  'radioOptionExtraPrice',
  'radioOptionViewDetails',
  'radioOptionDescription',
  'radioIndicator',
  'radioIndicatorDot'
];

export const optionStepBaseStyle = {
  slotCard: {
    alignItems: 'center',
    alignSelf: 'stretch',
    bg: 'white',
    border: '1px solid',
    borderColor: 'gray.100',
    borderRadius: 'base',
    display: 'flex',
    flexDirection: 'row',
    gap: 4,
    h: 'configurationSelectorSlotMinH',
    justifyContent: 'space-between',
    marginBottom: 3,
    minH: 'configurationSelectorSlotMinH',
    p: 4,
  },
  slotCardEmpty: {
    alignItems: 'center',
    alignSelf: 'stretch',
    bg: 'background.tertiary',
    border: '1px dashed',
    borderColor: 'grayAlpha.10',
    borderRadius: 'base',
    display: 'flex',
    flexDirection: 'row',
    gap: 4,
    h: 'auto',
    justifyContent: 'space-between',
    minH: 'configurationSelectorSlotMinH',
    p: 4,
    sx: {
      '&.customize-steps-slot-card .slot-card-choose-button': {
        fontSize: '12px',
        fontWeight: 600,
      },
    },
  },
  slotCardSelected: {
    border: '2px solid',
    borderColor: 'grayAlpha.90',
    sx: {
      /* slotCardSelected spreads only when selected; parent is customize-steps-option-step--multi-type. */
      '.customize-steps-option-step--multi-type & .slot-card-content-text-label': {
        color: 'grayAlpha.90',
        fontFamily: 'body',
        fontSize: '1xs',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: '1.2',
      },
      '.customize-steps-option-step--multi-type & .slot-card-content-text-name': {
        color: 'grayAlpha.90',
        fontFamily: 'body',
        fontSize: 'sm',
        fontStyle: 'normal',
        fontVariantNumeric: 'lining-nums proportional-nums',
        fontWeight: 600,
        lineHeight: '1.4',
      },
      '.slot-card-choose-button': {
        fontWeight: 600,
        fontSize: '12px',
      },
    },
  },
  slotCardImage: {
    aspectRatio: '14 / 11',
    borderRadius: 'md',
    flexShrink: 0,
    h: 'configurationSelectorSlotImageH',
    maxH: 'configurationSelectorSlotImageH',
    maxW: 28,
    objectFit: 'contain',
    w: 'auto',
  },

  /** Simple PDP slot card — matches flyout picker thumbnail when image missing. */
  slotCardImagePlaceholder: {
    aspectRatio: '48/55',
    backgroundColor: '#D1D1D1',
    borderRadius: 'md',
    flexShrink: 0,
    h: '55px',
    maxH: '55px',
    maxW: '48px',
    minH: '55px',
    minW: '48px',
    w: '48px',
  },
  slotCardChooseLink: {
    color: 'brandColors.viewDetailsLink',
    cursor: 'pointer',
    flexShrink: 0,
    h: 'auto',
    minH: '24px',
    minW: '24px',
    p: 0,
    textDecoration: 'none',
    textStyle: 'eyebrow12',
    _hover: {
      bg: 'transparent',
      color: 'brandColors.viewDetailsLink',
      opacity: 0.85,
    },
    _active: {bg: 'transparent'},
    _focus: {boxShadow: 'none'},
    _focusVisible: {
      boxShadow: 'none',
      outline: '2px solid',
      outlineColor: 'brandColors.primaryDarkLinen',
      outlineOffset: '2px',
    },
  },
  nameLine1: {
    color: 'grayAlpha.90',
    fontFamily: 'body',
    fontSize: '1xs',
    fontStyle: 'normal',
    fontVariantNumeric: 'lining-nums proportional-nums',
    lineHeight: 'base',
    noOfLines: 2,
    fontWeight: 400,
  },
  nameLine2: {
    color: 'grayAlpha.90',
    fontFamily: 'body',
    fontSize: 'md',
    fontStyle: 'normal',
    fontWeight: 600,
    lineHeight: 'shorter',
    marginTop: 0,
    noOfLines: 2,
    sx: {
      '.customize-steps-option-step--cushion &': {
        color: 'brandColors.greyscale90',
        fontSize: {base: '14px', lg: '16px'},
        fontWeight: 600,
        lineHeight: '140%',
      },
    },
  },
  nameLineEmptyTitle: {
    color: 'grayAlpha.90',
    fontStyle: 'normal',
    fontVariantNumeric: 'lining-nums proportional-nums',
    textStyle: 'labelDefault',
  },
  nameLineEmptyHint: {
    color: 'greyscale.70',
    marginTop: 0,
    textStyle: 'bodyS',
  },
  nameLineEmptySlotLabel: {
    color: 'greyscale.70',
    marginTop: 0,
    textStyle: 'bodyS',
  },
  radioOption: {
    alignItems: 'center',
    border: '1px solid',
    borderColor: 'grayAlpha.10',
    borderRadius: 'base',
    cursor: 'pointer',
    display: 'flex',
    gap: 4,
    px: {base: 0, lg: 4},
    py: 4,
    transition: 'border-color 0.15s ease',
    width: '100%',
    _hover: {
      borderColor: 'grayAlpha.20',
      backgroundColor: 'gray.50',
    },
    sx: {
      '&.option-slot-product-picker-item': {
        borderRadius: '0',
        borderLeft: 'none',
        borderRight: 'none',
        borderTop: 'none',
        borderColor: 'grayAlpha.10',
      },
    },
    _focusVisible: {
      boxShadow: 'none',
      outline: '2px solid',
      outlineColor: 'blue.500',
      outlineOffset: '2px',
    },
  },
  radioOptionSelected: {
    border: '2px solid',
    borderColor: 'grayAlpha.90',
    _hover: {
      borderColor: 'grayAlpha.90',
    },
    sx: {
      '&.option-slot-product-picker-item': {
        border: '2px solid',
        borderColor: 'grayAlpha.90',
        borderLeft: '2px solid',
        borderRight: '2px solid',
        borderTop: '2px solid',
      },
    },
  },
  radioOptionImage: {
    aspectRatio: '48/55',
    borderRadius: 'md',
    flexShrink: 0,
    h: 'auto',
    maxW: 12,
    objectFit: 'contain',
    w: 'auto',
  },
  radioOptionImagePlaceholder: {
    aspectRatio: '48/55',
    backgroundColor: '#D1D1D1',
    borderRadius: 'md',
    flexShrink: 0,
    h: 'auto',
    maxW: 12,
    w: 'auto',
  },
  radioOptionExtraPriceContainer: {
    flexShrink: 0,
    textAlign: 'center',
  },
  radioOptionExtraPrice: {
    color: 'greyscale.70',
    display: 'flex',
    fontFamily: 'body',
    fontSize: '1xs',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: 'shorter',
    textAlign: 'right',
    flexFlow: 'row wrap',
    gap: 1,
    maxW: 'configurationSelectorRadioExtraPriceMaxW',
    justifyContent: 'flex-end',
    sx: {
      '& span': {
        color: 'inherit',
        fontFamily: 'inherit',
        fontSize: '1xs',
        fontWeight: 400,
        lineHeight: 'inherit',
      },
    },
  },
  radioOptionViewDetails: {
    alignSelf: 'flex-start',
    color: 'brandColors.viewDetailsLink',
    cursor: 'pointer',
    display: {base: 'inline', lg: 'none'},
    fontFamily: 'body',
    fontSize: '1xs',
    fontStyle: 'normal',
    fontWeight: 600,
    lineHeight: 'shorter',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    width: 'fit-content',
    _hover: {
      color: 'brandColors.primaryDarkLinen',
    },
  },
  // KINDW-1124 — short description rendered under nameLine1 / nameLine2 inside
  // radio option cards (Configurable Master cushion-style cards, TSD §"Configurable
  // Options"). Subdued so the name lines remain the dominant element. Only renders
  // when the recommendation product carries `shortDescription` / `c_shortDescription`,
  // so existing single-line products are visually unchanged.
  radioOptionDescription: {
    color: 'grayAlpha.70',
    fontFamily: 'body',
    fontSize: 'xs',
    fontWeight: 400,
    lineHeight: 'short',
    sx: {
      '.configuration-panel-body-wrapper--customize-step--multi-type &': {
        display: 'none',
      },
    },
  },
  radioIndicator: {
    bg: 'white',
    alignItems: 'center',
    aspectRatio: '1 / 1',
    border: '1px solid',
    borderColor: 'brandColors.linen03',
    borderRadius: 'full',
    boxSizing: 'border-box',
    display: 'flex',
    flexShrink: 0,
    h: 8,
    justifyContent: 'center',
    w: 8,
  },
  radioIndicatorDot: {
    backgroundColor: 'brandColors.greyscale',
    borderRadius: 'full',
    flexShrink: 0,
    h: 5.5,
    w: 5.5,
  },
};
